/**
 * R10's liveness dot — how each state looks, separated from how it is decided.
 *
 * The Block D re-plan parked the dot at grey ("not yet reporting") and named its
 * alternative: *"the browser-side ping of the resolver edge's well-known endpoint, worth
 * doing at c39 when a public edge exists."* It exists now, so this is that.
 *
 * ## Where the split falls, and why it is not arbitrary
 *
 * `../api/edge.js` owns the request and the classification — what came back, and what it
 * means about the edge. That file is in `apps/app/src/api/` because c33's rule makes it
 * the SPA's only door to the network, and it carries the paragraph explaining why this is
 * the one request that must skip the org header.
 *
 * This file owns only the presentation: which CSS modifier and which sentence. A pure
 * mapping, testable as ordinary logic, with no knowledge of fetch.
 *
 * ## Why the presentation is worth its own tested function
 *
 * `apps/app`'s suite is `renderToStaticMarkup` — it fires no events and runs no effects —
 * so **every state except `unknown` is unreachable by a render, by construction.** That is
 * c37b's finding, and the rule it produced is that such a rendering owes an E2E flow
 * which *traverses* the gate rather than a static render of what lies behind it.
 *
 * What that rule does not require is that the *reasoning* also be unreachable. Keeping
 * the mapping pure means a change to the rule — a fourth state, a red one, an
 * optimistic default — is caught by a fast test, while the E2E flow proves the wire
 * actually reaches it. Two different claims, two different instruments.
 */
import type { EdgeLiveness } from "../api/edge.js";

export interface EdgeDotPresentation {
  /** Appended to `pp-dash__dot`. Empty for the two states that must stay grey. */
  readonly modifier: string;
  /** The sentence the user reads. The dot itself is decorative and `aria-hidden`. */
  readonly label: string;
}

/**
 * How each state presents.
 *
 * **Only `live` is coloured**, which is how this stays inside `screens.md` §2's
 * criterion — *"on a healthy dashboard exactly one thing is coloured."* Before c39 that
 * budget was unspent: the dot was grey and nothing else on a healthy screen carried a
 * tone, so "exactly one" described a thing that did not exist. The live dot is what it
 * was reserved for. On an *unhealthy* dashboard the recall tone governs by §12.1's
 * precedence stack, and a live dot beside it is not a competing claim — the edge being up
 * and a recall being active are both true, and the criterion binds the healthy screen.
 *
 * **`unreachable` is grey, not red**, and its wording says *from this browser* on
 * purpose. One failed fetch from one client is not evidence the edge is down; it is also
 * what a captive portal, a corporate proxy and a closed laptop lid look like. Claiming
 * the first when only the second is known is the same overreach R10 forbids, pointed the
 * other way — and red would additionally be a second coloured thing.
 *
 * The two grey states are told apart by the label rather than by a third shade, because a
 * colour that means two things needs prose.
 */
export function presentEdge(state: EdgeLiveness): EdgeDotPresentation {
  switch (state) {
    case "live":
      return {
        modifier: " pp-dash__dot--live",
        label: "Your resolver edge is answering.",
      };
    case "unreachable":
      return {
        modifier: "",
        label: "Couldn’t reach your resolver edge from this browser just now.",
      };
    case "unknown":
      return { modifier: "", label: "Checking your resolver edge…" };
  }
}
